// TMOS Role Selection Modal
// Date: January 16, 2026
// Purpose: Modal for selecting platform roles to associate with provisioning profiles

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ProvisionScope, PlatformRole, provisioningProfileService } from '@/services/provisioningProfileService';
import { pePortfolioService } from '@/services/pePortfolioService';
import { MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface RoleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
  onSelect: (roleId: string, provisionOrder: number, provisionScope: ProvisionScope) => void;
  existingRoleIds?: string[]; // Roles already associated with this profile
  lockProvisionScope?: ProvisionScope;
  /** When set (e.g. PE portfolio provisioning), loads roles via this callback instead of admin API. */
  loadRolesOverride?: () => Promise<PlatformRole[]>;
}

export const RoleSelectionModal: React.FC<RoleSelectionModalProps> = ({
  isOpen,
  onClose,
  profileId: _profileId, // Currently not used but kept for future use
  onSelect,
  existingRoleIds = [],
  lockProvisionScope,
  loadRolesOverride,
}) => {
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [provisionOrder, setProvisionOrder] = useState(0);
  const [provisionScope, setProvisionScope] = useState<ProvisionScope>('TENANT');

  // Load roles when modal opens
  useEffect(() => {
    if (lockProvisionScope) setProvisionScope(lockProvisionScope);
  }, [lockProvisionScope]);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setSearchTerm('');
      setSelectedRoleId(null);
      setProvisionOrder(0);
      setProvisionScope(lockProvisionScope ?? 'TENANT');
      loadRoles();
    } else {
      // Clear roles when modal closes
      setRoles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to avoid multiple calls

  const loadRoles = async () => {
    try {
      setLoading(true);
      let availableRoles: PlatformRole[];
      if (loadRolesOverride) {
        availableRoles = await loadRolesOverride();
      } else if (lockProvisionScope) {
        availableRoles = await pePortfolioService.getPePortfolioAvailableRoles('tenant');
      } else {
        availableRoles = await provisioningProfileService.getAvailableRoles();
      }
      
      // Filter out roles already associated with this profile
      const filteredRoles = availableRoles.filter(
        (role: PlatformRole) => !existingRoleIds.includes(role.id)
      );
      
      setRoles(filteredRoles);
      
      if (filteredRoles.length === 0 && availableRoles.length === 0) {
        console.warn('No platform roles found. Check backend console for query results.');
      }
    } catch (error: any) {
      console.error('Error loading roles:', error);
      toast.error(error.message || 'Failed to load roles');
      setRoles([]); // Clear roles on error
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = React.useMemo(() => {
    if (!searchTerm) {
      return roles; // Show all if no search term
    }
    
    const searchLower = searchTerm.toLowerCase();
    return roles.filter(role =>
      role.display_name?.toLowerCase().includes(searchLower) ||
      role.role_name?.toLowerCase().includes(searchLower) ||
      role.description?.toLowerCase().includes(searchLower)
    );
  }, [roles, searchTerm]);

  const handleSelect = () => {
    if (!selectedRoleId) {
      toast.error('Please select a role');
      return;
    }

    onSelect(selectedRoleId, provisionOrder, lockProvisionScope ?? provisionScope);
    // Reset form
    setSelectedRoleId(null);
    setProvisionOrder(0);
    setProvisionScope('TENANT');
    setSearchTerm('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Platform Role"
      size="lg"
    >
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[var(--color-secondary-text)] opacity-50" />
          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
          />
        </div>

        {/* Role List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-info)] mx-auto"></div>
            <p className="mt-2 text-sm text-[var(--color-secondary-text)]">Loading roles...</p>
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[var(--color-secondary-text)]">
              {searchTerm ? 'No roles match your search' : 'No available roles'}
            </p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredRoles.map((role) => (
              <div
                key={role.id}
                onClick={() => setSelectedRoleId(role.id)}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedRoleId === role.id
                    ? 'border-[var(--color-info)] bg-[var(--color-info)]/10'
                    : 'border-[var(--color-border)] hover:border-[var(--color-info)]/50 hover:bg-[var(--color-secondary-bg)]'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-[var(--color-surface-text)]">{role.display_name}</h4>
                      {selectedRoleId === role.id && (
                        <CheckIcon className="h-5 w-5 text-[var(--color-info)]" />
                      )}
                    </div>
                    {role.description && (
                      <p className="text-sm text-[var(--color-secondary-text)] mt-1">{role.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary">{role.role_name}</Badge>
                      <Badge variant="info">Platform</Badge>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Configuration Options */}
        {selectedRoleId && (
          <div className="border-t border-[var(--color-border)] pt-4 space-y-4">
            {!lockProvisionScope && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                Provision Scope
              </label>
              <select
                value={provisionScope}
                onChange={(e) => setProvisionScope(e.target.value as ProvisionScope)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
              >
                <option value="TENANT">Tenant Level (provisioned once per tenant)</option>
                <option value="USER">User Level (provisioned per user)</option>
              </select>
              <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                {provisionScope === 'TENANT' 
                  ? 'Role will be copied to tenant once (shared across all users)'
                  : 'Role will be copied to tenant for each user with this profile (not typical - roles are usually tenant-scoped)'}
              </p>
            </div>
            )}
            {lockProvisionScope === 'TENANT' && (
              <p className="text-xs text-[var(--color-secondary-text)]">
                Tenant level — role is copied once per portfolio company.
              </p>
            )}
            {lockProvisionScope === 'USER' && (
              <p className="text-xs text-[var(--color-secondary-text)]">
                User level on the administrator profile — provisioned per admin user on the tenant.
              </p>
            )}

            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">
                Provision Order
              </label>
              <input
                type="number"
                min="0"
                value={provisionOrder}
                onChange={(e) => setProvisionOrder(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                placeholder="0"
              />
              <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                Lower numbers are provisioned first (0 = first)
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!selectedRoleId || loading}
          >
            Add Role
          </Button>
        </div>
      </div>
    </Modal>
  );
};

